/**
 * Meta Conversions API (CAPI) configuration card.
 *
 * Two consumption modes:
 *   1. Account-level (mode="account") — calls GET/PUT
 *      /api/auth/me/capi-settings to read/write the user's default
 *      token. Used in ProfileSettings.
 *   2. Per-page (mode="page") — controlled component, parent owns
 *      the state and persists it inside page.settings.analytics.metaCapi
 *      via the page save flow. Used in Builder.tsx.
 *
 * Why share one component across both:
 *   - Same UX language ("paste token, send test, done")
 *   - Same field validation
 *   - Same "Test Events" debugging affordance
 *   - Single place to update if Meta deprecates a field
 *
 * Token UX intentionally mirrors Stripe / Vercel / Sendgrid:
 *   - Token is masked once stored (••••••••XXXX)
 *   - Editing requires re-pasting the full token (no partial edits)
 *   - "Disconnect" clears the token explicitly
 */

import React from 'react';
import toast from 'react-hot-toast';
import { Activity, AlertCircle, CheckCircle2, Loader2, Send, Eye, EyeOff } from 'lucide-react';

// Re-uses the in-app toggle for visual consistency with Cloaking/SEO.
import { ToggleSwitch } from './ToggleSwitch';
import { useAuthStore } from '../../store/authStore';

const API_URL = 'https://api.hitzlink.com/api';

// Canonical auth token accessor. The auth store persists under zustand
// key `auth-storage`; the raw JWT is also mirrored to localStorage as
// `token` (legacy code reads it that way). DON'T read from localStorage
// keys like `auth-token` / `auth_token` — those don't exist.
function getAuthToken(): string {
  // Prefer the live store value (always fresh after login/logout).
  const fromStore = useAuthStore.getState().token;
  if (fromStore) return fromStore;
  // Fallback for the small window between page reload and store rehydrate.
  return localStorage.getItem('token') || '';
}

/**
 * Build a display-safe masked token that *proves* to the operator the
 * stored secret is intact, without leaking it.
 *
 * Example: formatMaskedToken('EAARo4', 'ZDZD', 187) → 'EAARo4••••••••ZDZD'
 *
 * Why prefix + suffix + length together:
 *   - "EAA" prefix catches wrong-token-type mistakes (System User tokens
 *     start with EAA too but page access tokens don't; a human can spot
 *     a copy-paste swap immediately).
 *   - Suffix matches Stripe/Vercel UX so users know *which* credential
 *     is stored when they have multiple.
 *   - Length confirms the paste wasn't truncated. Meta CAPI tokens are
 *     ~180–240 chars; a 60-char value is almost certainly broken.
 */
function formatMaskedToken(prefix: string, suffix: string): string {
  if (!prefix && !suffix) return '';
  const safePrefix = prefix || '';
  const safeSuffix = suffix || '';
  return `${safePrefix}••••••••${safeSuffix}`;
}

export interface PageCapiValue {
  accessToken?: string;
  testEventCode?: string;
  enabled?: boolean;
}

interface BaseProps {
  /** Optional pixel id used to verify "Send test event". When the
   *  parent doesn't have a pixel id handy (e.g. account-level page
   *  where the user might be configuring CAPI before any pixel id),
   *  the button is disabled until they paste one. */
  defaultPixelId?: string;
}

interface AccountModeProps extends BaseProps {
  mode: 'account';
}

interface PageModeProps extends BaseProps {
  mode: 'page';
  /** Current per-page CAPI settings (controlled). */
  value: PageCapiValue;
  onChange: (next: PageCapiValue) => void;
  /** When true, the section shows a hint that an account-level
   *  default exists and per-page values override it. */
  hasAccountDefault?: boolean;
}

type Props = AccountModeProps | PageModeProps;

export const MetaCapiSettings: React.FC<Props> = (props) => {
  const isAccount = props.mode === 'account';

  // Account-mode local state (loaded from server). For page-mode we
  // use props.value directly.
  const [loading, setLoading] = React.useState(isAccount);
  const [hasAccessToken, setHasAccessToken] = React.useState(false);
  const [maskedToken, setMaskedToken] = React.useState('');
  // Token provenance (used to render prefix + length reassurance badges).
  const [tokenPrefix, setTokenPrefix] = React.useState('');
  const [tokenSuffix, setTokenSuffix] = React.useState('');
  const [tokenLength, setTokenLength] = React.useState(0);

  // Form state — what the user is currently typing. For account mode
  // this gets PUT'd on save; for page mode this is mirrored to
  // props.onChange on every change.
  const [tokenInput, setTokenInput] = React.useState('');
  const [testCode, setTestCode] = React.useState('');
  const [enabled, setEnabled] = React.useState(true);
  const [showToken, setShowToken] = React.useState(false);
  const [editingToken, setEditingToken] = React.useState(false);
  const [pixelInput, setPixelInput] = React.useState(props.defaultPixelId || '');
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState<null | { ok: boolean; msg: string }>(null);

  // ── Account mode: load existing settings on mount ───────────────
  React.useEffect(() => {
    if (!isAccount) return;
    let cancelled = false;
    (async () => {
      try {
        const token = getAuthToken();
        const res = await fetch(`${API_URL}/auth/me/capi-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('failed to load');
        const data = await res.json();
        if (cancelled) return;
        setHasAccessToken(Boolean(data.hasAccessToken));
        setMaskedToken(data.accessTokenMasked || '');
        setTokenPrefix(data.accessTokenPrefix || '');
        setTokenSuffix(data.accessTokenSuffix || '');
        setTokenLength(Number(data.accessTokenLength) || 0);
        setTestCode(data.testEventCode || '');
        setEnabled(data.enabled ?? Boolean(data.hasAccessToken));
      } catch {
        // swallow — first-time users will just see empty form
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAccount]);

  // ── Page mode: mirror controlled props into local state once ────
  React.useEffect(() => {
    if (props.mode !== 'page') return;
    const tok = props.value?.accessToken || '';
    setTokenInput(tok);
    setTestCode(props.value?.testEventCode || '');
    setEnabled(props.value?.enabled ?? Boolean(tok));
    // The mask makes no sense for page-level values that the parent
    // already holds in plaintext (parent may persist them however it
    // wants — typically inside page.settings JSONB). Treat it as
    // "configured if non-empty".
    setHasAccessToken(Boolean(tok));
    // Derive prefix/suffix/length locally — page mode has the full
    // token in-memory (parent owns it) so we don't need a round trip.
    setTokenPrefix(tok ? tok.slice(0, 6) : '');
    setTokenSuffix(tok ? tok.slice(-4) : '');
    setTokenLength(tok.length);
  }, [props.mode === 'page' ? JSON.stringify(props.value) : null]);

  // Page-mode emits on every change.
  function emitPageChange(patch: Partial<PageCapiValue>) {
    if (props.mode !== 'page') return;
    props.onChange({
      accessToken: patch.accessToken ?? props.value?.accessToken,
      testEventCode: patch.testEventCode ?? props.value?.testEventCode,
      enabled: patch.enabled ?? props.value?.enabled,
    });
  }

  // ── Save (account mode only) ────────────────────────────────────
  async function handleSaveAccount() {
    setSaving(true);
    try {
      const token = getAuthToken();
      const body: Record<string, unknown> = {
        testEventCode: testCode,
        enabled,
      };
      if (editingToken || !hasAccessToken) {
        body.accessToken = tokenInput.trim();
      }
      const res = await fetch(`${API_URL}/auth/me/capi-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'save failed');
      setHasAccessToken(data.hasAccessToken);
      if (data.hasAccessToken && tokenInput) {
        const trimmed = tokenInput.trim();
        setTokenPrefix(trimmed.slice(0, 6));
        setTokenSuffix(trimmed.slice(-4));
        setTokenLength(trimmed.length);
        setMaskedToken(formatMaskedToken(trimmed.slice(0, 6), trimmed.slice(-4)));
      }
      setEditingToken(false);
      setTokenInput('');
      toast.success('CAPI settings saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // ── Disconnect (account mode only) ──────────────────────────────
  async function handleDisconnectAccount() {
    if (!confirm('Remove the saved access token? You can re-paste it any time.')) return;
    setSaving(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/auth/me/capi-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ accessToken: '', enabled: false }),
      });
      if (!res.ok) throw new Error('disconnect failed');
      setHasAccessToken(false);
      setMaskedToken('');
      setTokenPrefix('');
      setTokenSuffix('');
      setTokenLength(0);
      setTokenInput('');
      setEnabled(false);
      setEditingToken(false);
      toast.success('Disconnected');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  // ── Test event ──────────────────────────────────────────────────
  async function handleTest() {
    setTestResult(null);
    setTesting(true);
    try {
      const token = getAuthToken();
      const tokenForTest =
        tokenInput.trim() ||
        (props.mode === 'page' ? props.value?.accessToken : '') ||
        '';
      if (!tokenForTest) {
        throw new Error('Paste an access token first (or use the saved one by editing it).');
      }
      if (!pixelInput.trim()) {
        throw new Error('Enter a Pixel ID to test against.');
      }
      if (!testCode.trim()) {
        throw new Error('Test Event Code required. Get one from Events Manager → Test Events tab.');
      }
      const res = await fetch(`${API_URL}/capi/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pixelId: pixelInput.trim(),
          accessToken: tokenForTest,
          testEventCode: testCode.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Meta rejected');
      setTestResult({
        ok: true,
        msg: `Sent. Open Events Manager → Test Events tab. fbtrace_id: ${data.fbtrace_id || '—'}`,
      });
    } catch (err) {
      setTestResult({
        ok: false,
        msg: err instanceof Error ? err.message : 'Test failed',
      });
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm py-6">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading CAPI settings…
      </div>
    );
  }

  // Visual style is light-on-dark to match Builder; ProfileSettings
  // wraps this in a white card so the inner controls inherit the
  // surrounding background colour from the parent.
  const themeIsLight = isAccount;
  const labelCls = themeIsLight
    ? 'block text-sm font-medium text-gray-700 mb-2'
    : 'block text-sm font-medium text-gray-300 mb-2';
  const inputCls = themeIsLight
    ? 'w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500'
    : 'w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500';
  const helperCls = themeIsLight ? 'text-xs text-gray-500 mt-1' : 'text-xs text-gray-500 mt-1';

  return (
    <div className="space-y-4">
      {/* Header / status badge */}
      <div className="flex items-center gap-2">
        <Activity className={themeIsLight ? 'w-5 h-5 text-emerald-600' : 'w-5 h-5 text-emerald-400'} />
        <h4 className={themeIsLight ? 'text-base font-semibold text-gray-900' : 'text-base font-semibold text-white'}>
          Meta Conversions API
        </h4>
        {hasAccessToken && enabled && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-emerald-500/15 text-emerald-500">
            <CheckCircle2 className="w-3 h-3" /> Active
          </span>
        )}
      </div>
      <p className={themeIsLight ? 'text-sm text-gray-600' : 'text-sm text-gray-400'}>
        Server-side event tracking. Bypasses ad-blockers and iOS 14.5 limits.
        Events are sent in parallel with the browser Pixel and deduplicated by Meta.
      </p>

      {/* Account-default hint when in page mode */}
      {props.mode === 'page' && (props as PageModeProps).hasAccountDefault && !hasAccessToken && (
        <div className={themeIsLight
          ? 'flex gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-3'
          : 'flex gap-2 text-sm text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3'}>
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Using your account-level CAPI token. Paste a different token here only if you want this
            specific page to use a separate Pixel + token pair.
          </span>
        </div>
      )}

      {/* Access token */}
      <div>
        <label className={labelCls}>Conversions API Access Token</label>
        {hasAccessToken && !editingToken ? (
          <>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={maskedToken || formatMaskedToken(tokenPrefix, tokenSuffix)}
                disabled
                className={inputCls + ' opacity-70 font-mono'}
              />
              <button
                type="button"
                onClick={() => { setEditingToken(true); setTokenInput(''); }}
                className="px-3 py-2 text-sm rounded-lg border border-gray-400 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Replace
              </button>
            </div>
            {/* Provenance line: confirms the stored token is intact without revealing it.
                Prefix/length together rule out paste-truncation and wrong-token-type mistakes. */}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500">
                <CheckCircle2 className="w-3 h-3" /> saved
              </span>
              {tokenPrefix && (
                <span className={themeIsLight ? 'text-gray-500' : 'text-gray-400'}>
                  starts with <span className="font-mono">{tokenPrefix}</span>
                </span>
              )}
              {tokenLength > 0 && (
                <>
                  <span className={themeIsLight ? 'text-gray-400' : 'text-gray-500'}>·</span>
                  <span className={themeIsLight ? 'text-gray-500' : 'text-gray-400'}>
                    {tokenLength} chars
                  </span>
                </>
              )}
              {/* Short tokens (<150 chars) are almost always truncated pastes.
                  Surface a gentle warning — not an error — because some older
                  Meta accounts do issue shorter tokens. */}
              {tokenLength > 0 && tokenLength < 150 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500">
                  <AlertCircle className="w-3 h-3" /> looks short — re-paste if "Send test" fails
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type={showToken ? 'text' : 'password'}
              value={tokenInput}
              onChange={(e) => {
                setTokenInput(e.target.value);
                if (props.mode === 'page') emitPageChange({ accessToken: e.target.value });
              }}
              placeholder="EAA…"
              className={inputCls}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => setShowToken((s) => !s)}
              className="p-2 text-gray-500 hover:text-gray-700"
              aria-label={showToken ? 'Hide token' : 'Show token'}
            >
              {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        )}
        <p className={helperCls}>
          Get it from <span className="font-mono">Events Manager → Settings → Conversions API → Generate access token</span>.
          Token is stored encrypted server-side and never sent back to the browser.
        </p>
      </div>

      {/* Pixel ID input — only when CAPI is being tested in account mode (page mode already knows its pixels). */}
      {isAccount && (
        <div>
          <label className={labelCls}>Pixel ID for Test Event</label>
          <input
            type="text"
            value={pixelInput}
            onChange={(e) => setPixelInput(e.target.value)}
            placeholder="1234567890"
            className={inputCls}
          />
          <p className={helperCls}>Used only by "Send test event" — your landing pages each define their own Pixel IDs.</p>
        </div>
      )}

      {/* Test event code */}
      <div>
        <label className={labelCls}>Test Event Code (optional)</label>
        <input
          type="text"
          value={testCode}
          onChange={(e) => {
            setTestCode(e.target.value);
            if (props.mode === 'page') emitPageChange({ testEventCode: e.target.value });
          }}
          placeholder="TEST12345"
          className={inputCls}
        />
        <p className={helperCls}>
          From <span className="font-mono">Events Manager → Test Events</span>. Required for "Send test event"; clear it before going live.
        </p>
      </div>

      {/* Enable toggle */}
      <div className={themeIsLight ? 'pt-2 border-t border-gray-200' : 'pt-2 border-t border-gray-700'}>
        <ToggleSwitch
          label="Enable server-side tracking"
          description="Send PageView and Lead events from your server in parallel with browser Pixel."
          checked={enabled}
          onChange={(v) => {
            setEnabled(v);
            if (props.mode === 'page') emitPageChange({ enabled: v });
          }}
        />
      </div>

      {/* Test result panel */}
      {testResult && (
        <div className={
          (testResult.ok
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
            : 'bg-red-500/10 border-red-500/30 text-red-400'
          ) + ' border rounded-lg p-3 text-sm flex gap-2'
        }>
          {testResult.ok
            ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
          <span>{testResult.msg}</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 pt-2">
        <button
          type="button"
          onClick={handleTest}
          disabled={testing || !testCode.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send test event
        </button>
        {isAccount && (
          <>
            <button
              type="button"
              onClick={handleSaveAccount}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Save
            </button>
            {hasAccessToken && (
              <button
                type="button"
                onClick={handleDisconnectAccount}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium"
              >
                Disconnect
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
